import { css } from '@emotion/css';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { ReactComponent as Icon } from 'src/assets/images/fortune/ic-nookpoint.svg';
import { ConfirmButton } from 'src/components/shared/buttons/ConfirmButton';
import { NormalButton } from 'src/components/shared/buttons/NormalButton';
import { Dialog } from 'src/components/shared/dialog/Dialog';
import { FadeInImage } from 'src/components/shared/FadeInImage';
import { RootState } from 'src/features';
import { actions } from 'src/features/fortune';
import { formatNumber } from 'src/lib/format-number';
import { sendEvent } from 'src/lib/google-analytics';
import { throttle } from 'src/lib/utils';
import { catalogIconUrl } from 'src/models/catalog/CatalogItem';
import { FortuneItem } from 'src/models/fortune/FortuneItem';

export const FortuneExchangeConfirmDialog: React.FC = () => {
  const { exchangeableItem, point } = useSelector((state: RootState) => state.fortune);

  const params = useParams<{ id: string }>();
  const uniqueId = getUniqueIdAsNum(params.id);

  // workaround: ダイアログを閉じる際、URLのパラメータやクエリを参照できなくなってFortuneExchangeConfirmContentが非表示になり、
  // ダイアログの高さが変わるのを防ぐために、画面描画にはStateに格納したアイテム情報を使用する
  const [itemSnapshot, setItemSnapshot] = useState<FortuneItem | undefined>(undefined);

  useEffect(() => {
    if (exchangeableItem?.state === 'ok') {
      if (!itemSnapshot) {
        const targetItem = exchangeableItem.result.items.find(item => item.unique_id === uniqueId);
        setItemSnapshot(targetItem);
      }
    }
  }, [exchangeableItem, itemSnapshot, uniqueId]);

  if (point?.state !== 'ok') {
    return <Redirect to="/" />;
  }

  return (
    <Dialog hasHeader={true}>
      {itemSnapshot && (
        <FortuneExchangeConfirmContent
          item={itemSnapshot}
          currentPoint={point.result.currentPoint}
        />
      )}
    </Dialog>
  );
};

interface ContentProps {
  item: FortuneItem;
  currentPoint: number;
}

// local component
const FortuneExchangeConfirmContent: React.FC<ContentProps> = ({ item, currentPoint }) => {
  const { t, i18n } = useTranslation(['fortune', 'translation']);
  const history = useHistory();
  const dispatch = useDispatch();
  const succeededDialogPath = `/players/fortune/exchange/detail/${item.unique_id}/succeeded`;
  const failedDialogPath = `/players/fortune/exchange/detail/${item.unique_id}/failed`;

  const { exchangeStatus } = useSelector((state: RootState) => state.fortune);

  useEffect(() => {
    return () => {
      dispatch(actions.resetExchangeStatus());
    };
  }, [dispatch]);

  useEffect(() => {
    switch (exchangeStatus?.state) {
      case 'ok':
        history.replace(succeededDialogPath);
        break;
      case 'error':
        const failedDialogPathWithError = failedDialogPath + `?error=${exchangeStatus.error}`;
        history.replace(failedDialogPathWithError);
        break;
    }
  }, [exchangeStatus, failedDialogPath, history, succeededDialogPath]);

  const handleSubmitClick = useCallback(
    throttle(() => {
      if (item) {
        const unique_id = item.unique_id;
        const uniqueIdStr = unique_id.toString();
        const remake_id = item.remake_id;
        dispatch(actions.exchangeFortuneItem({ unique_id, remake_id }));
        sendEvent('fortune_do_exchange', 'click', uniqueIdStr);
      }
    }, 1000),
    [dispatch]
  );

  const handleCancelClick = useCallback(
    throttle(() => {
      const uniqueIdStr = item.unique_id.toString();
      history.goBack();
      sendEvent('fortune_cancel_exchange', 'click', uniqueIdStr);
    }, 1000),
    [history]
  );

  const isLoading = exchangeStatus?.state === 'pending';

  if (!item) {
    return <></>;
  }

  return (
    <div
      className={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        color: #493a31;

        @media screen and (max-width: 374px) {
          font-size: 14px;
        }
        h1 {
          font-size: 20px;
          text-align: center;
          margin-bottom: 12px;
        }
        .item-info {
          display: flex;
          align-items: center;
          width: 100%;
          margin-bottom: 12px;
        }
        .label {
          margin-left: 8px;
          font-size: 16px;
        }
        .required-point {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          color: #33a142;
          padding-bottom: 8px;
          margin-bottom: 16px;
          border-bottom: 2px dashed #dcd8bf;
        }
        .required-point-label {
          font-size: 14px;
        }
        .required-point-value {
          display: flex;
          align-items: center;
          font-size: 16px;
          margin-left: 2px;
          font-size: 24px;
        }
        .owned-point {
          align-self: flex-end;
          display: flex;
          align-items: center;
          color: #938666;
          font-size: 14px;
        }
        .owned-point-value {
          display: flex;
          align-items: center;
          margin-left: 4px;
        }
      `}
    >
      <h1>{t('dialog_confirm_exchange')}</h1>
      <div className="item-info">
        <FadeInImage
          src={catalogIconUrl(item.icon)}
          width={48}
          height={48}
          style={{ width: '48px', height: '48px' }}
          ariaLabel={t('aria_detail_image')}
        />
        <p className="label">{item.label}</p>
      </div>
      <div
        className="required-point"
        aria-labelledby={'dialog-required-point-label dialog-required-point-value'}
      >
        <p id="dialog-required-point-label">{t('required_points')}</p>
        <div className="required-point-value">
          <Icon width={24} height={24} />
          <span id="dialog-required-point-value">{formatNumber(item.point, i18n.language)}</span>
        </div>
      </div>
      <div className="owned-point">
        <p>{t('owned_point')}</p>
        <div className="owned-point-value">
          <Icon width={24} height={24} />
          <p>{formatNumber(currentPoint, i18n.language)}</p>
        </div>
      </div>

      <div
        className={css`
          width: 100%;
          margin-top: 24px;
          margin-bottom: 16px;
        `}
      >
        <ConfirmButton onClick={handleSubmitClick} loading={isLoading}>
          {t('translation:dialog_button_ok')}
        </ConfirmButton>
      </div>
      <NormalButton onClick={handleCancelClick} disabled={isLoading}>
        {t('translation:dialog_button_cancel')}
      </NormalButton>
    </div>
  );
};

// helper function
function getUniqueIdAsNum(numStr: string) {
  const parsed = parseInt(numStr, 10);

  if (isNaN(parsed)) {
    return undefined;
  }
  return parsed;
}
